<template>
  <!-- TODO: As part of the rework, we need to rename this component to be more accurate -->
  <div>
    <details-header v-if="showHeader"></details-header>
    <div
      data-testid="appointment-details-header"
      class="mb-8 appointment-details-wrapper d-flex align-center"
      v-if="editable">
      <router-link :to="{ path: `/warehouse/${$warehouseId}/appointments` }"
        >Back to Appointments
      </router-link>
      <v-spacer></v-spacer>
      <appointment-action-group
        v-if="appointmentWarehouse"
        :appointment-warehouse="appointmentWarehouse"
        :appointment="appointment"
        :appointment-actions="headerActions">
      </appointment-action-group>
    </div>

    <v-card class="appointment-details-wrapper guest-appointment-wrapper">
      <v-card-title>
        <status-manager
          class="full-width"
          :appointment="appointment"
          :should-edit-status="shouldEditStatus"
          mixpanel-entry-point="Carrier Portal"
          dense>
        </status-manager>
        <template v-if="!novaCore.isRequested(appointment)">
          <eta-button-group
            v-if="appointment"
            @condition-updated="updateCondition"
            :read-only="!canUpdateEta"
            :appointment="appointment"
            v-model="etaCondition"></eta-button-group>
        </template>
        <template v-else>
          <div v-if="appointment" class="full-width text-center">
            <v-icon small color="error" class="mr-1">mdi-alert-circle-outline</v-icon>
            <span class="font-size-x-small font-weight-bold">
              Please wait for the warehouse to confirm your appointment.
            </span>
          </div>
        </template>
      </v-card-title>
      <v-divider></v-divider>
      <v-card-text>
        <v-container fluid class="pa-0">
          <v-row>
            <v-col class="pa-12">
              <div>
                <div class="mx-2 mb-8">
                  <div class="d-flex">
                    <v-icon class="text-lg-h5 black--text mr-2">mdi-calendar</v-icon>
                    <div class="text-lg-h6 black--text" @click="handleDateHeaderClick">
                      {{ formatDateTime(appointment.start, 'dddd, MMM D, YYYY', true) }}
                      <span class="mx-2 d-inline-block>">&bull;</span>
                      {{ getAppointmentInterval(appointment) }}
                    </div>
                  </div>
                </div>

                <v-simple-table dense class="appointment-details-table" v-if="!loading">
                  <template v-slot:default>
                    <thead></thead>
                    <tbody>
                      <tr>
                        <td class="font-weight-bold pb-4">Confirmation #</td>
                        <td class="pb-4">
                          <div>
                            <span class="mr-4">{{ appointment.confirmationNumber }}</span>
                            <secondary-button
                              v-if="canCopy"
                              x-small
                              @click="copyValueToClipboard(appointment.confirmationNumber)">
                              Copy
                            </secondary-button>
                          </div>
                        </td>
                      </tr>
                      <tr>
                        <td class="font-weight-bold pb-4">Appointment ID</td>
                        <td class="pb-4">
                          <div>
                            <span class="mr-4" data-testid="appointment-details-id">{{
                              appointment.id
                            }}</span>
                            <secondary-button v-if="canCopy" x-small @click="copyApptId">
                              Copy
                            </secondary-button>
                          </div>
                        </td>
                      </tr>
                      <template v-for="(row, index) in appointmentTableRows">
                        <template v-if="!row.isHidden">
                          <tr :key="index + '-detail'">
                            <template>
                              <td class="font-weight-bold" :class="row.tdClass">
                                {{ row.label }}
                              </td>
                              <td :class="row.tdClass">
                                <div v-html="row.value"></div>
                              </td>
                            </template>
                          </tr>
                        </template>
                      </template>

                      <template v-for="customField in Object.values(customFields)">
                        <tr v-if="showCustomField(customField)" :key="customField.key">
                          <td class="font-weight-bold" :class="customField.tdClass">
                            {{ customField.label }}
                          </td>
                          <td :class="customField.tdClass">
                            <div v-html="customField.value" class="text-pre-wrap"></div>
                          </td>
                        </tr>
                      </template>
                      <tr>
                        <td class="font-weight-bold">Appointment Notes:</td>
                        <td>
                          <div class="appointment-notes" v-html="appointment.notes || '----'"></div>
                        </td>
                      </tr>
                    </tbody>
                  </template>
                </v-simple-table>
              </div>
            </v-col>

            <v-divider vertical></v-divider>

            <v-col class="pa-12">
              <div class="d-flex flex-column">
                <div>
                  <span class="font-weight-bold pb-2 d-flex align-center">
                    <v-icon class="mr-2">mdi-truck-outline</v-icon>
                    Carrier Contact
                  </span>
                  <span class="d-block font-weight-bold pb-1">{{ companyName }}</span>
                  <span class="d-flex align-center pb-1">
                    <v-icon class="mr-2" small>mdi-account</v-icon>
                    Name: {{ getUserName(appointment.user) }}
                  </span>
                  <span class="d-flex align-center pb-1">
                    <v-icon class="mr-2" small>mdi-email</v-icon>
                    Email: {{ appointment.user.email }}
                  </span>
                  <span class="d-flex align-center pb-1">
                    <v-icon class="mr-2" small>mdi-phone</v-icon>
                    Phone: {{ formatPhoneNumber(appointment.user.phone) || '----' }}
                  </span>
                  <span class="d-flex align-center pb-1">
                    <v-icon class="mr-2" small>mdi-truck-check</v-icon>
                    SCAC: {{ scac }}
                  </span>
                </div>

                <div>
                  <span class="font-weight-bold mt-12 pb-2 d-flex align-center">
                    <v-icon class="mr-2">mdi-warehouse</v-icon>
                    Warehouse
                  </span>
                  <span class="d-block font-weight-bold pb-1">{{
                    appointment.dock.warehouse.name
                  }}</span>
                  <span class="d-flex align-center pb-1">
                    <v-icon class="mr-2" small>mdi-account</v-icon>
                    <copy-content
                      :content="getWarehouseAddress(appointment.dock.warehouse)"
                      label="Click to copy warehouse address"
                      >{{ getWarehouseAddress(appointment.dock.warehouse) }}</copy-content
                    >
                  </span>
                  <span class="d-flex align-center pb-1">
                    <v-icon class="mr-2" small>mdi-email</v-icon>
                    {{ appointment.dock.warehouse.email }}
                  </span>
                  <span class="d-flex align-center pb-1">
                    <v-icon class="mr-2" small>mdi-phone</v-icon>
                    {{ formatPhoneNumber(appointment.dock.warehouse.phone) }}
                  </span>
                </div>
              </div>
            </v-col>
          </v-row>
        </v-container>
      </v-card-text>

      <template v-if="isCancelled">
        <div class="d-flex justify-center my-8">
          <v-btn
            elevation="0"
            class="text__error font-weight-bold"
            color="white"
            :disabled="isCancelled">
            <v-icon class="mr-2">{{ cancelButtonIcon }}</v-icon>
            {{ cancelAppointmentLabel }}
          </v-btn>
        </div>
      </template>

      <v-alert color="error" class="white--text" v-if="!appointment && !loading">
        Could not load appointment data
      </v-alert>

      <template v-if="loading">
        <v-card-text>
          Loading Appointment Data...
          <v-progress-linear color="secondary" indeterminate rounded height="8">
          </v-progress-linear>
        </v-card-text>
      </template>

      <reschedule-appointment-dialog
        v-if="appointmentWarehouse"
        :appointment-warehouse="appointmentWarehouse"
        @close="showRescheduleDialog = false"
        :external-activator="true"
        :show-dialog="showRescheduleDialog"
        :appointment="appointment">
      </reschedule-appointment-dialog>
    </v-card>

    <slot name="footer-actions"></slot>
  </div>
</template>

<script>
import appointmentMixin from '@/components/mixins/appointmentMixin';
import { DateTime } from 'luxon';

/**
 * @displayName Appointment Details
 */
export default {
  mixins: [appointmentMixin],
  props: {
    showHeader: {
      type: Boolean,
      required: false,
      default: true
    },
    /**
     * Appointment to display details for
     */
    appointment: {
      type: Object,
      required: true
    },
    /**
     * Display status editing options
     */
    shouldEditStatus: {
      type: Boolean,
      required: false,
      default: false
    },
    editable: {
      type: Boolean,
      required: false,
      default: true
    },
    appointmentWarehouse: {
      type: Object,
      required: true
    }
  },
  computed: {
    useMilitaryTime() {
      // Extends appointmentMixin property to declare a different way of looking into it
      return this.$isMilitaryTimeEnabled(this.$warehouse);
    },
    headerActions() {
      return {
        share: {
          icon: 'account-multiple',
          action: 'openShareDialog',
          show: this.canUpdate,
          tooltip: 'Share'
        },
        print: {
          icon: 'printer',
          action: 'openPrintTab',
          tooltip: 'Print',
          show: true
        },
        reschedule: {
          icon: 'calendar-edit',
          action: 'openRescheduleDialog',
          tooltip: 'Reschedule',
          show:
            this.canUpdate &&
            this.canReschedule &&
            this.novaCore.getReschedulableStatuses().includes(this.appointment.status)
        },
        edit: {
          icon: 'pencil-outline',
          action: 'openEditDialog',
          show: this.canUpdate,
          tooltip: 'Edit'
        }
      };
    },
    initialEtaCondition() {
      return (
        this.novaCore.getEtaCondition(
          this.appointment.start,
          this.appointment.eta,
          this.appointment.dock.warehouse.timezone
        ) ?? this.novaCore.EtaCondition.OnTime
      );
    },
    canUpdateEta() {
      const isWithin24HourWindow = this.novaCore.isEtaOnWindow(
        this.appointment.start,
        DateTime.utc().toJSDate()
      );

      return isWithin24HourWindow && this.canUpdate;
    }
  },
  data() {
    return {
      loading: true,
      tags: this.appointment.tags,
      lockTagEditing: true,
      etaCondition: '',
      showRescheduleDialog: false,
      appointmentTableRows: {
        dockName: {
          label: 'Dock Name',
          value: this.appointment.dock.name,
          tdClass: 'pb-4',
          isHidden: false
        },
        loadType: {
          label: 'Load Type',
          value: '',
          tdClass: 'pb-4',
          isHidden: false
        },
        refNumber: {
          label: 'Reference Number',
          value: this.appointment.refNumber || '----',
          tdClass: 'pb-4',
          isHidden: false
        }
      }
    };
  },
  methods: {
    getRequiredValue(field) {
      this.novaCore.getRequiredValue(field, false);
    },
    handleDateHeaderClick() {
      if (this.canUpdate && this.canReschedule) {
        this.showRescheduleDialog = true;
      }
    },
    async updateAppointment() {
      this.lockTagEditing = true;
      await axios
        .patch(`appointment/${this.appointment.id}`, {
          tags: this.tags
        })
        .finally(() => (this.lockTagEditing = false));
    },
    initializeRowValues() {
      this.appointmentTableRows.loadType.value = this.$loadTypeName(
        this.appointment.loadType,
        false,
        true
      );
      this.appointmentTableRows.refNumber.isHidden =
        !this.$consolidatedSettings.referenceNumberIsVisible;
      this.appointmentTableRows.refNumber.label =
        this.$consolidatedSettings.referenceNumberDisplayName;
      this.appointmentTableRows.refNumber.value = this.appointment.refNumber || 'N/A';
      this.appointmentTableRows.dockName.value = this.appointment.dock.name;
    },
    updateCondition(newCondition) {
      this.etaCondition = newCondition;
    }
  },
  async mounted() {
    this.initializeRowValues();
    this.setCustomFieldsRows();
    this.loading = false;
  },
  watch: {
    tags() {
      if (JSON.stringify(this.tags) !== JSON.stringify(this.appointment.tags)) {
        this.updateAppointment();
      }
    },
    appointment(appointment) {
      this.initializeRowValues();
      this.setCustomFieldsRows();
      this.tags = appointment.tags;
    },
    initialEtaCondition: {
      handler(newVal) {
        this.etaCondition = newVal;
      },
      immediate: true
    }
  }
};
</script>

<style lang="scss">
.appointment-notes > p:not(:only-child) {
  margin-bottom: 1px !important;
}
</style>
